import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'
import { useShallow } from 'zustand/shallow'

type State = {
  sideCheckedIds: number[]
  sideIsActive: boolean | undefined
  // Firma, do ktorej naleza zaznaczone id. Trzymane razem z zaznaczeniem, bo store
  // jest persystowany w sessionStorage i przezywa przelaczenie firmy oraz reload.
  sideCompanyId: string | undefined
}

type Actions = {
  toggleSide: (id: number, isActive?: boolean) => void
  toggleAllSide: (ids: number[], checked: boolean, isActive?: boolean) => void
  clearSide: () => void
  syncCompany: (companyId: string) => void
}

export const useSideSelectionStore = create<State & Actions>()(
  devtools(
    persist(
      (set, get) => ({
        sideCheckedIds: [],
        sideIsActive: undefined,
        sideCompanyId: undefined,

        toggleSide: (id, isActive) =>
          set(
            (s) => {
              const next = s.sideCheckedIds.includes(id)
                ? s.sideCheckedIds.filter((x) => x !== id)
                : [...s.sideCheckedIds, id]

              let sideIsActive = s.sideIsActive
              if (next.length === 0) sideIsActive = undefined
              else if (s.sideCheckedIds.length === 0) sideIsActive = isActive

              return { sideCheckedIds: next, sideIsActive }
            },
            false,
            'toggleSide',
          ),

        toggleAllSide: (ids, checked, isActive) =>
          set(
            {
              sideCheckedIds: checked ? ids : [],
              sideIsActive: checked ? isActive : undefined,
            },
            false,
            'toggleAllSide',
          ),

        clearSide: () =>
          set(
            { sideCheckedIds: [], sideIsActive: undefined },
            false,
            'clearSide',
          ),

        // Wolane przy kazdej zmianie companyId w DriversProvider (a ten montuje sie w
        // kilku modulach). Czysci zaznaczenie TYLKO gdy firma faktycznie sie zmienila -
        // inaczej kasowaloby je przy kazdym montowaniu providera, takze po odswiezeniu
        // strony w tej samej firmie, co zniweczyloby sens persystowania.
        //
        // Wczesny return zamiast set(...) zwracajacego pusty obiekt: zustand porownuje
        // przez Object.is, wiec set({}) zbudowalby nowy obiekt stanu i powiadomil
        // wszystkich subskrybentow (a spora czesc komponentow czyta caly store bez
        // selektora), a persist przy okazji serializowalby stan do sessionStorage.
        syncCompany: (companyId) => {
          if (get().sideCompanyId === companyId) return

          set(
            {
              sideCheckedIds: [],
              sideIsActive: undefined,
              sideCompanyId: companyId,
            },
            false,
            'syncCompany',
          )
        },
      }),
      {
        name: 'driver-side-selection',
        storage: createJSONStorage(() => sessionStorage),
        partialize: (s) => ({
          sideCheckedIds: s.sideCheckedIds,
          sideIsActive: s.sideIsActive,
          sideCompanyId: s.sideCompanyId,
        }),
      },
    ),
    { name: 'EasyTacho', store: 'side-selection' },
  ),
)

export const useSideSelection = () =>
  useSideSelectionStore(
    useShallow((s) => ({
      sideCheckedIds: s.sideCheckedIds,
      sideIsActive: s.sideIsActive,
      toggleSide: s.toggleSide,
      toggleAllSide: s.toggleAllSide,
      clearSide: s.clearSide,
      syncCompany: s.syncCompany,
    })),
  )
